import React, { useEffect, useState } from "react";
import { Skeleton } from "@mui/material";
import api from "../../api/api";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { showToast } from "../../reduxtoolkit/toastslice";
import { getPracticeId } from "../../config/practice";

const RoomPopup = ({setScreen,selectedDate , selectedAppointment ,fetchScheduleData,closeAllModals}) => {
  const [selectedRoom, setSelectedRoom] = useState(null); // Selected room name
  const [providers, setProviders] = useState([]); // Room data from API
  const [groupedProviders, setGroupedProviders] = useState({}); // Grouped by type
  const [loading, setLoading] = useState(false); // Loading state
  const [occupiedList, setOccupiedList] = useState([]);
  const [confirmReassign, setConfirmReassign] = useState(null); // { room, type, occupant }
  const practiseid = getPracticeId();
  const dispatch = useDispatch();

  const getOccupantName = (occupant) =>
    [occupant?.firstname, occupant?.lastname]
      .filter(Boolean)
      .join(" ")
      .trim() || "Another patient";

  // Fetch data from the API
  const fetchdata = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/v1/${practiseid}/rooms/get`);
      if (response.status === 200) {
        setProviders(response.data); // Accessing the data array
        // Group providers by type
        const grouped = response.data.reduce((acc, room) => {
         if (room.type !== 'North' && room.type !== 'South') {
            if (!acc[room.type]) {
              acc[room.type] = [];
            }
            acc[room.type].push(room);
          }
          return acc;
        }, {});
        setGroupedProviders(grouped);
      } else {
        setProviders([]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOccupied = async () => {
    try {
      const dt =
        (selectedAppointment?.startdate &&
          dayjs(selectedAppointment.startdate).format("YYYY-MM-DD")) ||
        (selectedAppointment?.pc_eventDate &&
          dayjs(selectedAppointment.pc_eventDate).format("YYYY-MM-DD")) ||
        (selectedDate && dayjs(selectedDate).format("YYYY-MM-DD")) ||
        dayjs().format("YYYY-MM-DD");

      // Pull data from BOTH endpoints — schedule_list covers scheduled (not-yet-checked-in)
      // patients, future/get covers checked-in patients.
      const [scheduleRes, checkinRes] = await Promise.all([
        api
          .post(`/v1/${practiseid}/scheduler/schedule_list`, { startdate: dt })
          .catch(() => null),
        api
          .post(`/v1/${practiseid}/checkin/future/get`, {
            stdate: dt,
            eddate: dt,
          })
          .catch(() => null),
      ]);

      const merged = [];
      const seen = new Set();
      const pushAll = (arr) => {
        (arr || []).forEach((p) => {
          if (!p) return;
          const key = `${p.patientid || p.pc_pid}-${p.appointmentid || p.pc_eid}`;
          if (seen.has(key)) return;
          seen.add(key);
          merged.push({
            patientid: p.patientid || p.pc_pid,
            appointmentid: p.appointmentid || p.pc_eid,
            firstname: p.firstname || p.patient_firstname,
            lastname: p.lastname || p.patient_lastname,
            room_no: p.room_no || p.roomno || "",
            startdate: p.startdate || p.pc_eventDate,
            token: p.token,
            id: p.id,
            linked_provider: p.linked_provider,
          });
        });
      };
      if (scheduleRes?.status === 200) pushAll(scheduleRes.data);
      if (checkinRes?.status === 200) pushAll(checkinRes.data);

      setOccupiedList(merged.filter((p) => p.room_no));
    } catch (e) {
      console.log(e);
    }
  };

  const unassignOccupant = async (occupant) => {
    try {
      await api.post(
        `/v1/${practiseid}/checkin/patient/${occupant?.patientid}/unassign`,
        {
          appointment_id: occupant?.appointmentid,
          token: occupant?.token,
          roomno: occupant?.room_no,
          rowid: occupant?.id,
        }
      );
      return true;
    } catch (err) {
      console.log("Unassign error", err);
      return false;
    }
  };

  // Fetch data when popup opens
  useEffect(() => {
      fetchdata();
      fetchOccupied();
  }, []);

  // Handle room button click — block reassign of own room, confirm if occupied
  const handleButtonClick = (roomName, roomType) => {
    const occupied = occupiedList.find(
      (item) =>
        item.room_no === roomName &&
        String(item.patientid) !== String(selectedAppointment?.patientid)
    );

    if (occupied) {
      setConfirmReassign({ room: roomName, type: roomType, occupant: occupied });
      return;
    }

    setSelectedRoom({ roomName, roomType });
  };

  const handleConfirmReassign = async () => {
    if (!confirmReassign) return;
    const { room, type, occupant } = confirmReassign;
    setConfirmReassign(null);
    setLoading(true);
    const ok = await unassignOccupant(occupant);
    setLoading(false);
    if (!ok) {
      dispatch(
        showToast({
          message: "Failed to unassign the previous patient",
          severity: "error",
        })
      );
      return;
    }
    setSelectedRoom({ roomName: room, roomType: type });
    await handleResignRoom({ roomName: room, roomType: type });
    await fetchOccupied();
  };

// Define the mapping for room types to names
const roomTypeToName = {
  "Suite 106": "Madhu",
  "Suite 107": "Jaime",
  "Suite 105": "Oco"
};

const handleResignRoom = async (selectedRoom) => {
  if (selectedRoom) {
    const { roomName, roomType } = selectedRoom;
    const payload = {
      roomno: roomName, // Room number
      appointmentid: selectedAppointment.appointmentid, // Appointment ID
      roomtype: roomType // Room type
    };

    try {
      const response = await api.put(`/v1/${practiseid}/rooms/patients/${selectedAppointment.patientid}/insert`, payload);
      if (response.status === 200) {
        dispatch(
          showToast({
            message: "Room Assigned Successfully",
            severity: "success",
          })
        );
        fetchScheduleData(selectedDate); // Assuming this function fetches the updated schedule
        setScreen(0);
      } else {
        dispatch(
          showToast({
            message: response?.message || "Failed to assign room",
            severity: "info",
          })
        );
      }
    } catch (error) {
      console.error("Error assigning room:", error);
      dispatch(
        showToast({
          message: "Something Went Wrong",
          severity: "error",
        })
      );
    }
  }
};

  return (

      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        {/* Popup Box */}
        <div className="relative flex flex-col gap-4 w-[35%] bg-white   rounded-lg shadow-lg text-left">
          {/* Header */}
          <div>
            <div className="w-full flex items-center justify-between mt-2 px-4 py-2">
              <span className="ml-2 font-inter font-bold text-[#111827] text-base">
              Assign Room
              </span>
              <button
                className="text-gray-500 hover:text-gray-700 text-[30px]"
                onClick={()=>{
                setScreen(0)
              }}
              >
                &times;
              </button>
            </div>
            <div className="h-px bg-[#D0D5DD]"></div>
          </div>

          {/* Grouped Room Buttons */}
          <div className="flex flex-col gap-4 max-h-[440px] overflow-y-auto custom-scrollbar p-4">
            {loading
              ? [...Array(6)].map((_, index) => (
                  <Skeleton
                    key={index}
                    variant="rectangular"
                    width={100}
                    height={40}
                    sx={{ borderRadius: "20px" }}
                  />
                ))
              : Object.entries(groupedProviders).map(([type, rooms]) => (
                  <div key={type} className="flex flex-col gap-2">
                    <span className="font-inter text-[13px] font-Font1 text-[#111827]">  {roomTypeToName[type] || type}:</span>
                    <div className="flex flex-wrap gap-4">
                      {rooms.map((room, index) => {
                        const isActive =
                          selectedRoom &&
                          selectedRoom.roomName === room.room &&
                          selectedRoom.roomType === room.type;
                        const occupant =
                          String(room.room).toLowerCase() !== "waiting room"
                            ? occupiedList.find(
                                (item) =>
                                  item.room_no === room.room &&
                                  String(item.patientid) !==
                                    String(selectedAppointment?.patientid)
                              )
                            : null;
                        const isOccupied = !!occupant;
                        return (
                          <button
                            key={index}
                            onClick={() => handleButtonClick(room.room, room.type)}
                            title={
                              isOccupied
                                ? `${room.room} is currently being used by ${getOccupantName(
                                    occupant
                                  )}`
                                : ""
                            }
                            className={`rounded-full border-[1.5px] text-sm font-inter py-2 px-4 transition duration-300 ease-in-out
                              ${
                                isOccupied
                                  ? "bg-[#FEF3C7] text-[#92400E] border-[#F59E0B]"
                                  : isActive
                                    ? "bg-[#E17954] text-white border-gray-200"
                                    : "bg-[#F3F3F3] text-[#1D2951] border-gray-200 hover:bg-[#E17954] hover:text-[#FFFFFF]"
                              }`}
                          >
                            {room.room}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
          </div>
         {/* Update Button */}
<div className="flex items-center justify-center border-t bg-[#F9FAFB] p-3 rounded-[10px]">
<button  onClick={()=>handleResignRoom(selectedRoom)}
  className={`bg-[#18181B] text-white rounded-[10px] py-2 px-4 w-[90%] transition duration-300 ease-in-out
    ${selectedRoom ? 'bg-[#black] text-[#FFFFFF] cursor-pointer' : 'bg-black border-2 text-gray-500 cursor-not-allowed'}
  `}
>
Assign Room
</button>

</div>


        </div>

        {confirmReassign && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center">
            <div
              className="absolute inset-0 bg-black/40"
              onClick={() => setConfirmReassign(null)}
              aria-hidden
            />
            <div className="relative z-10 w-[420px] max-w-[92vw] rounded-2xl bg-white shadow-2xl p-6">
              <div className="flex flex-col items-center text-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#FEF3C7] flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#B45309"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                    <line x1="12" y1="9" x2="12" y2="13" />
                    <line x1="12" y1="17" x2="12.01" y2="17" />
                  </svg>
                </div>
                <div className="text-[16px] font-semibold font-inter text-[#1D2951]">
                  Room Already Assigned
                </div>
                <div className="text-[14px] font-inter text-[#4B5563]">
                  <span className="font-semibold">{confirmReassign.room}</span>{" "}
                  is currently being used by{" "}
                  <span className="font-semibold">
                    {getOccupantName(confirmReassign.occupant)}
                  </span>
                  .
                  <br />
                  Do you want to reassign it?{" "}
                  <span className="font-semibold">
                    {getOccupantName(confirmReassign.occupant)}
                  </span>{" "}
                  will be unassigned first.
                </div>
                <div className="flex items-center justify-center gap-3 mt-2 w-full">
                  <button
                    onClick={() => setConfirmReassign(null)}
                    className="h-[38px] flex-1 rounded-full border border-[#D1D5DB] text-[14px] font-medium text-[#374151] bg-white font-inter"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmReassign}
                    className="h-[38px] flex-1 rounded-full bg-[#E17954] text-[14px] font-medium text-white font-inter"
                  >
                    Reassign
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

  );
};

export default RoomPopup;
